// src/hooks/usePageContext.ts
/**
 * usePageContext Hook
 * Detects current page type and extracts relevant IDs from URL
 */

import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { Id } from '../../convex/_generated/dataModel';
import type { IPageContext, PageType } from '@/@types/globalChat';

/**
 * Hook to detect page context from current URL
 * Returns page type and relevant entity IDs
 */
export function usePageContext(): IPageContext {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const pathname = location.pathname;

    // Detect Property Video page: /property-video OR /property/new (both render PropertyVideoPage)
    if (
      pathname === '/property-video' ||
      pathname.startsWith('/property-video') ||
      pathname === '/property/new' ||
      pathname.startsWith('/property/new')
    ) {
      const propertyId = searchParams.get('propertyId') as Id<'propertyListings'> | null; // AS_JUSTIFIED: route param from URL string — only entry point for this Id type
      const videoId = searchParams.get('videoId') as Id<'propertyVideos'> | null; // AS_JUSTIFIED: route param from URL string — only entry point for this Id type

      return {
        pageType: 'property-video' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
        propertyId,
        videoId,
      };
    }

    // Detect Reels page (future)
    if (pathname === '/reels' || pathname.startsWith('/reels')) {
      return {
        pageType: 'reels' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
        propertyId: null,
        videoId: null,
      };
    }

    // Detect Posts page (future)
    if (pathname === '/posts' || pathname.startsWith('/posts')) {
      return {
        pageType: 'posts' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
        propertyId: null,
        videoId: null,
      };
    }

    // Detect Insurance pages: /insurance/*
    if (pathname.startsWith('/insurance')) {
      return {
        pageType: 'insurance' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
        propertyId: null,
        videoId: null,
      };
    }

    // Detect Real-Estate pages: /real-estate/*
    // NOTE: /property/* is handled above as 'property-video'; /real-estate/* is a separate domain
    if (pathname.startsWith('/real-estate')) {
      return {
        pageType: 'real-estate' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
        propertyId: null,
        videoId: null,
      };
    }

    // Default: other page
    return {
      pageType: 'other' as PageType, // AS_JUSTIFIED: string literal narrowed to PageType union — safe exhaustive cast
      propertyId: null,
      videoId: null,
    };
  }, [location.pathname, searchParams]);
}
